import type { ReactNode } from "react";
import { useEffect } from "react";

import { getPageMetadata } from "@pm2/api";
import {
  findPageDescriptorById,
  findPageDescriptorByPath,
  pm2SitemapStore,
} from "@pm2/sitemap-utils";
import { pm2ConfigStore } from "@pm2/store";
import { configureAppContextProvider, appContext } from "@provider/app-context";
import { useLocation } from "@provider/navigation";

export function AppContextProvider(props: {
  children: ReactNode | ReactNode[];
}): React.JSX.Element {
  const sitemap = pm2SitemapStore.useSelector((state) => state);
  const location = useLocation();

  const previewMode = pm2ConfigStore.useSelector((state) => state.previewMode);
  const portalConfig = pm2ConfigStore.useSelector((state) => state.config);

  useEffect(() => {
    // In case the title has been prefixed with 'Gyldendals fagportaler' in the
    // backend, then remove this part as it is not part of the actual title.
    const portalTitle = portalConfig.title.replace(
      / \| Gyldendals fagportaler$/,
      "",
    );

    const contextState = {
      app: {
        name: "PM2",
      },
      publisher: portalConfig.publisher ?? "gyldendal",
      product: {
        id: portalConfig.id,
        isbn: portalConfig.uniqueId,
        type: "pm2",
        title: portalTitle,
        subject: portalConfig.theme.logo.subject,
        gradesLabel: portalConfig.theme.logo.grades,
        classLabel: portalConfig.theme.logo.classLabel,
        audience: portalConfig.theme.targetAudience,
      },
    } as const;

    appContext.dispatch(appContext.actions.init(contextState));

    configureAppContextProvider({
      async getPageAuthors(product, page): Promise<string[] | undefined> {
        let pageDescriptor = findPageDescriptorById(sitemap, page.id);

        // recursively scan up-wards in the sitemap in order to find a course
        // that contains the currently active page

        while (pageDescriptor && pageDescriptor?.type !== "Course") {
          pageDescriptor = pageDescriptor.parentId
            ? findPageDescriptorById(sitemap, pageDescriptor.parentId)
            : undefined;
        }

        // if we didn't find a course for the page, then we cannot resolve
        // authors for the page in any way
        if (!pageDescriptor) {
          return undefined;
        }

        const metaData = await getPageMetadata(product.id, pageDescriptor.id, {
          previewMode,
        });

        return metaData.metadata?.contributors
          ?.filter((it) => it.role === "forfatter")
          .map((it) => it.entity);
      },
    });
  }, [
    portalConfig.id,
    portalConfig.publisher,
    portalConfig.theme.logo.classLabel,
    portalConfig.theme.logo.grades,
    portalConfig.theme.logo.subject,
    portalConfig.theme.targetAudience,
    portalConfig.title,
    portalConfig.uniqueId,
    previewMode,
    sitemap,
  ]);

  useEffect(() => {
    const pageDescriptor = findPageDescriptorByPath(sitemap, location.pathname);
    let courseDescriptor = pageDescriptor;

    while (courseDescriptor && courseDescriptor.type !== "Course") {
      courseDescriptor = courseDescriptor.parentId
        ? findPageDescriptorById(sitemap, courseDescriptor.parentId)
        : undefined;
    }

    const pageContext = pageDescriptor
      ? ({
          id: pageDescriptor.id,
          type: (() => {
            switch (pageDescriptor.type) {
              case "Course":
                return "course";

              case "Chapter":
              case "ChapterGroup":
                return "chapter";

              default:
                return "page";
            }
          })(),
          title: pageDescriptor.title,
        } as const)
      : undefined;

    const courseContext = courseDescriptor
      ? ({
          id: courseDescriptor.id,
          title: courseDescriptor.title,
          type: "course",
        } as const)
      : undefined;

    appContext.dispatch(
      appContext.actions.update({ page: pageContext, course: courseContext }),
    );
  }, [location.asPath, location.pathname, sitemap]);

  return <>{props.children}</>;
}
